import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { FaWifi, FaSnowflake, FaCocktail, FaBed, FaTv, FaConciergeBell } from 'react-icons/fa';
import rooms from '../data/rooms';

const getAmenityIcon = (amenity) => {
  switch (amenity.toLowerCase()) {
    case 'wifi': return <FaWifi />;
    case 'ac': return <FaSnowflake />;
    case 'tv': return <FaTv />;
    case 'balcony': return <FaBed />;
    case 'living area': return <FaBed />;
    case 'extra bed': return <FaBed />;
    case '1 king bed':
    case '1 twin bed':
    case '2 twin beds':
    case '1 twin bed + 1 king bed':
    case '2 king beds':
      return <FaBed />;
    default: return <FaConciergeBell />;
  }
};

const RoomDetail = () => {
  const { id } = useParams();
  const room = rooms.find(r => r.id === parseInt(id));

  if (!room) {
    return (
      <motion.div
        className="py-16 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        Room not found
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-highlight">
      {/* Hero Image Section */}
      <motion.div
        className="relative h-screen flex items-center justify-center text-center text-white"
        style={{ backgroundImage: `url(${room.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <motion.h1
            className="text-5xl md:text-7xl font-serif font-bold mb-4 drop-shadow-lg"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            {room.name}
          </motion.h1>
          <motion.p
            className="text-2xl md:text-3xl mb-8 drop-shadow"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            ${room.price} per night
          </motion.p>
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <a
            target='_blank'
              href="https://www.booking.com/hotel/np/admire-pokhara-pvt-ltd.html?aid=2405612&label=brave_nonbrand_organic_trigger_f3f828ac-4c6a-4b54-8e46-e2779b841553&sid=cd36b88c5418b460034b77c1a1c04aa8&age=0&checkin=2025-12-08&checkout=2025-12-09&dest_id=-1022488&dest_type=city&dist=0&group_adults=2&group_children=1&hapos=1&hpos=1&no_rooms=1&req_adults=2&req_age=0&req_children=1&room1=A%2CA%2C0&sb_price_type=total&soh=1&sr_order=popularity&srepoch=1765214882&srpvid=947b7ac8086705c8&type=total&ucfs=1&#room_467069404"
              className="bg-accent hover:bg-accent/90 text-primary font-serif font-bold py-4 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-block"
            >
              Book Now
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Details Section */}
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            className="bg-background rounded-xl shadow-xl p-8 mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="text-3xl font-serif font-bold mb-6 text-primary">Room Description</h2>
            <p className="text-lg text-secondary leading-relaxed">{room.description}</p>
          </motion.div>

          <motion.div
            className="bg-background rounded-xl shadow-xl p-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2 className="text-3xl font-serif font-bold mb-6 text-primary">Amenities</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {room.amenities.map((amenity, index) => (
                <motion.div
                  key={index}
                  className="flex items-center bg-highlight rounded-lg p-4 hover:bg-accent/20 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="text-2xl text-accent mr-3">
                    {getAmenityIcon(amenity)}
                  </div>
                  <span className="font-semibold text-primary">{amenity}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RoomDetail;